import { createPasswordPolicy, PasswordPolicy } from '@acme/b2b/security';
import { passwordValidatorFactory } from '@acme/b2b/validators';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, TODO } from '@b2b/types/core';
import uiRouter, { StateParams } from '@uirouter/angularjs';
import angular, { IFilterService, ILogService, IScope, ITimeoutService } from 'angular';
import invariant from 'tiny-invariant';

AccountProfileController.$inject = [
  '$scope',
  '$stateParams',
  '$log',
  '$timeout',
  '$filter',
  'ApiService',
  'AuthenticationService',
  'HelpersService',
  'ValidateService',
  'NoticeFactory',
  'UserPreferencesService',
  'ThemesHelperService',
  'ThemesData',
];

function AccountProfileController(
  $scope: IScope & { accountProfile: TODO },
  $stateParams: StateParams,
  $log: ILogService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  apiService: ApiService,
  authenticationService: AuthenticationService,
  helpersService: TODO,
  validateService: TODO,
  NoticeFactory: NoticeFactory,
  userPreferencesService: TODO,
  themesHelperService: ThemesHelperService,
  ThemesData: { data: ACMEResponse<ThemesData> }
) {
  const ctrl = {
    changePassword: false,
    isSubmitted: false,
    showErrors: false,

    passwordPolicy: null as PasswordPolicy | null,

    newPasswordMessage: '',
    confirmPasswordMessage: '',
    currentPasswordMessage: '',
    currentImage: '',

    model: {
      confirmPassword: '',
      user: {
        firstName: '',
        lastName: '',
        password: '',
        oldPassword: '',
        dateFormat: '',
        hourFormat: '',
        startDayOfWeek: '',
        accessibilityEnabled: false,
        profileImage: null,
        phoneNumber: '',
      } as TODO,
    },

    userSettings: userPreferencesService.settings,

    buildDate: function (format: string) {
      return format + ' [  ' + $filter('date')(new Date(), format) + ' ]';
    },

    notice: new NoticeFactory(),

    init: function () {
      $scope.$emit('Application::contentTitleChange', {
        title: 'My Account',
        buttonRight: {
          label: 'Update',
          permissions: 'PROFILE_UPDATE',
          action: () => {
            ctrl.complete();
          },
        },
      });
      $scope.$on('upload::done', ctrl.onUploadDone);
      $scope.$on('upload::error', ctrl.onUploadError);

      ctrl.model.user = authenticationService.getUserFromCookie();

      if (ThemesData && ThemesData.data.success) {
        themesHelperService.setModel(ThemesData.data.payload);
        ctrl.passwordPolicy = createPasswordPolicy({
          passwordSecurityPolicyEnabled: ThemesData.data.payload.company.passwordSecurityPolicyEnabled,
          passwordMinimumLength: ThemesData.data.payload.company.passwordMinimumLength,
          passwordUseMixedCase: ThemesData.data.payload.company.passwordUseMixedCase,
          passwordMinimumDigits: ThemesData.data.payload.company.passwordMinimumDigits,
          passwordMinimumSymbols: ThemesData.data.payload.company.passwordMinimumSymbols,
        });
      }

      invariant(ctrl.model.user.id != null);

      ctrl.getUser(ctrl.model.user.id);
    },

    setChangePassword(value: 'start' | 'cancel') {
      ctrl.changePassword = value === 'start';
    },

    complete: function () {
      const validators: TODO[] = [
        { name: ctrl.model.user.firstName },
        { name: ctrl.model.user.lastName },
        { len: { value: ctrl.model.user.title } },
        { phone: ctrl.model.user.phoneNumber },
      ];

      ctrl.isSubmitted = false;

      if (ctrl.changePassword === true) {
        const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
        const passwordValidator = passwordValidatorFactory(passwordPolicy);
        validators.push({
          predicate: {
            test: { predicateFn: () => passwordValidator(ctrl.model.user.password) == null },
            message: 'Please enter a valid password',
          },
        });
        validators.push({ equal: [ctrl.model.user.password, ctrl.model.confirmPassword] });
      }

      if (validateService.validate(validators)) {
        ctrl.notice.hideSuccessMessages();
        ctrl.notice.hideErrorMessages();
        apiService.user
          .update(ctrl.model.user)
          .then((response) => ctrl.success.complete(response))
          .catch((response) => ctrl.error.complete(response));
      } else {
        ctrl.isSubmitted = true;

        ctrl.notice.showErrorMessages([{ message: 'Please correct the errors below.' }]);
      }
    },

    getUser: function (userId: number) {
      apiService.user
        .view(userId)
        .then((response) => ctrl.success.getUser(response))
        .catch((response) => ctrl.error.getUser(response));
    },

    isValidCurrentPassword: function () {
      let result = true;

      if (ctrl.isSubmitted && ctrl.changePassword) {
        result = validateService.validate([
          {
            field: 'currentPassword',
            len: {
              message: 'Please enter a valid password',
              test: () => {
                return { value: ctrl.model.user.oldPassword };
              },
            },
          },
        ]);
      }

      if (!result) {
        ctrl.currentPasswordMessage = validateService.errors[0].message;
      }

      return result;
    },

    isValidNewPassword: function () {
      const passwordPolicy = createPasswordPolicy(ctrl.passwordPolicy ?? {});
      const passwordValidator = passwordValidatorFactory(passwordPolicy);
      let result = true;

      if (ctrl.isSubmitted && ctrl.changePassword) {
        result = validateService.validate([
          {
            field: 'password',
            predicate: {
              test: { predicateFn: () => passwordValidator(ctrl.model.user.password) == null },
              message: 'Please enter a valid password',
            },
          },
        ]);
      }

      if (!result) {
        ctrl.newPasswordMessage = validateService.errors[0].message;
      }

      return result;
    },

    isValidConfirmPassword: function () {
      let r = true;

      if (ctrl.isSubmitted && ctrl.changePassword) {
        r = validateService.validate([
          {
            field: 'confirmPassword',
            equal: {
              test: () => [ctrl.model.user.password, ctrl.model.confirmPassword],
              message: 'Passwords must be the same',
            },
          },
        ]);
      }

      if (!r) {
        ctrl.confirmPasswordMessage = validateService.errors[0].message;
      }

      return r;
    },

    isValidTitle: function () {
      if (!ctrl.notice.isLoaded) {
        // Don't test undefined values
        return true;
      }

      return validateService.validate([{ len: { value: ctrl.model.user.title } }]);
    },

    isValidFirstName: function () {
      return ctrl.isValidName(ctrl.model.user.firstName);
    },

    isValidLastName: function () {
      return ctrl.isValidName(ctrl.model.user.lastName);
    },

    isValidPhone: function () {
      if (!ctrl.notice.isLoaded) {
        // Don't test undefined values
        return true;
      }

      return validateService.validate([{ phone: ctrl.model.user.phoneNumber }]);
    },

    isValidName: function (value: string) {
      if (!ctrl.notice.isLoaded) {
        // Don't test undefined values
        return true;
      }

      return validateService.validate([{ name: value }]);
    },

    processUserSettings: function (settings: TODO, user: TODO) {
      const r = { hourFormat: '', dateFormat: '', startDayOfWeek: '' };

      for (const key in r) {
        if (r.hasOwnProperty(key)) {
          r[key] = user[key] ? user[key] : settings[key] ? settings[key] : '';
        }
      }
      return r;
    },

    isSSO: function () {
      return authenticationService.isSSO();
    },

    success: {
      complete: function (response: { data: TODO }) {
        ctrl.changePassword = false;
        ctrl.showErrors = false;

        ctrl.isSubmitted = true;
        userPreferencesService.process(null, response.data.user);
        ctrl.model.user.oldPassword = '';
        ctrl.model.user.password = '';
        ctrl.model.confirmPassword = '';

        ctrl.notice.showSuccessMessages([{ message: 'Successfully Saved!' }], { duration: 3000 });
      },

      getUser: function (response: { data: TODO }) {
        const settings = {
          startDayOfWeek: userPreferencesService.settings.startDayOfWeek[6],
          dateFormat: userPreferencesService.getDateFormat('shortDate'),
          hourFormat: userPreferencesService.getHourFormat(),
        };

        ctrl.notice.hideErrorMessages();
        ctrl.notice.isLoaded = true;

        if (settings) {
          const user = ctrl.processUserSettings(settings, response.data.user);
          helpersService.decorate(response.data.user, user);
          helpersService.decorate(ctrl.model.user, response.data.user);
        } else {
          helpersService.decorate(ctrl.model.user, response.data.user);
        }

        if ($stateParams.changePassword) {
          ctrl.setChangePassword('start');
        }
      },
    },

    error: {
      complete: function (response: { data: TODO }) {
        ctrl.isSubmitted = true;

        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      getUser: function (response: { data: TODO }) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },
    },

    removeProfileImage: function () {
      $scope.$broadcast('upload::delete', ctrl.currentImage);
      ctrl.model.user.profileImage = null;
      delete ctrl.model.user.profileImage;
      ctrl.complete();
    },

    onUploadDone: function (e: TODO, data: TODO) {
      ctrl.currentImage = data;
      ctrl.model.user.profileImage = data.file.uuid;
      ctrl.complete();
    },

    onUploadError: function (e: TODO, data: TODO) {
      ctrl.notice.showErrorMessages([{ message: data.result.error || 'There was an error uploading the file.' }]);
    },
  };

  $scope.accountProfile = ctrl;
  ctrl.init();
}

export const AccountProfileControllerModule = angular
  .module('acme.view.application.account.profile.controllers', [
    uiRouter,
    'acme.common.service.api',
    AuthenticationServiceModule,
    'acme.common.service.helpers',
    'acme.common.service.validate',
    'acme.common.service.userPreferences',
    'acme.common.service.themesHelper',
    'acme.common.factory.notice',
    'acme.common.directive.inputField',
    NoticesComponentModule,
  ])
  .controller('AccountProfileController', AccountProfileController).name;
